import aws4interceptor from 'aws4-axios';
import defaultAxios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { get, isEmpty, merge } from 'lodash';
import * as qs from 'qs';
import { IRequestAdapter, RequestInputs } from './RequestAdapter';

export const SIGNING_HOST_HEADER = 'x-sig4-signing-host';
export const SIGNING_PATH_HEADER = 'x-sig4-signing-path';

type RequestInterceptor = (config: AxiosRequestConfig) => Promise<AxiosRequestConfig>;
type InterceptorConfig = Parameters<typeof aws4interceptor>;

const REGION = 'us-east-1';
const SERVICE = 'execute-api';

const defaultInterceptor = aws4interceptor({
  region: REGION,
  service: SERVICE,
});

function getUrlToSign(config: AxiosRequestConfig): string | void {
  if (config.url) {
    const url = new URL(config.url);

    if (get(config.headers, SIGNING_HOST_HEADER)) {
      url.hostname = config.headers![SIGNING_HOST_HEADER] as string;
    }

    if (get(config.headers, SIGNING_PATH_HEADER)) {
      url.pathname = config.headers![SIGNING_PATH_HEADER] as string;
    }

    return url.toString();
  }
}

/**
 * Wraps the aws4-axios interceptor to allow signing with a different URL than the one that will be used for the request.
 * If an x-sig4-signing-host header is added, signing will use that host. If an x-sig4-signing-path header is added, that
 * path will be used for signing. After signing the request will be made to the original URL.
 * @param interceptor An aws4-axios interceptor
 * @returns An axios interceptor that overrides the domain used for signing
 */
export function urlWrapper(interceptor: RequestInterceptor): (config: AxiosRequestConfig) => Promise<AxiosRequestConfig> {
  return async (config) => {
    const url = getUrlToSign(config);
    if (config.url && url && url !== config.url) {
      const originalUrl = new URL(config.url);
      const { hostname: Host } = new URL(url);
      const configToSign = merge({}, config, { url, headers: { Host } });
      const signedConfig = await interceptor(configToSign);

      return merge({}, signedConfig, {
        url: originalUrl.toString(),
        headers: {
          Host: originalUrl.host,
        },
      });
    } else {
      const newConfig = await interceptor(config);
      return newConfig;
    }
  };
}

export function createSignedAxios(...interceptorArgs: InterceptorConfig) {
  const signedAxios = defaultAxios.create();
  const interceptor = isEmpty(interceptorArgs) ? defaultInterceptor : aws4interceptor(...interceptorArgs);
  signedAxios.interceptors.request.use(urlWrapper(interceptor));
  return signedAxios;
};

/**
 * @internal
 */
export class SignedAxiosAdapter implements IRequestAdapter {
  static create(...interceptorArgs: InterceptorConfig): SignedAxiosAdapter {
    return new SignedAxiosAdapter(createSignedAxios(...interceptorArgs));
  }

  axiosInstances: Record<string, AxiosInstance> = {};

  constructor(private axios: AxiosInstance) {
  }

  private axiosForRole(role?: string) {
    if (!role) {
      return this.axios;
    }

    if (!(role in this.axiosInstances)) {
      this.axiosInstances[role] = createSignedAxios({
        region: REGION,
        service: SERVICE,
        assumeRoleArn: role,
      });
    }

    return this.axiosInstances[role];
  }

  async request({ data, headers, role, ...rest }: RequestInputs) {
    const axios = this.axiosForRole(role);
    const options: any = { headers, ...rest };

    if (data) {
      if (typeof data === 'object') {
        if (headers['Content-Type'] === 'application/x-www-form-urlencoded') {
          options.data = qs.stringify(data);
        } else {
          options.data = data;
        }
      } else {
        options.data = data;
      }
    }

    try {
      const response = await axios(options);
      return response;
    } catch (error) {
      // @ts-ignore TODO
      if (!error.response) {
        this.axios = createSignedAxios();
      }

      throw error;
    }
  }
}
